/**
 * The collection of features and their permissions in IPP.
 */
export type AppFeatureDictionary = AppFeatureGroup[]

/**
 * Represents group of alike features that live together in the same domain or area of IPP.
 *
 * For example...
 *
 * platformSettings: {            <-- FeatureGroup
 *  viewRetailers: {...},         <-- AppFeature
 *  performanceDashboard: {...},  <-- AppFeature
 *  developerDashboard: {...},    <-- AppFeature
 * }
 *
 */
export interface AppFeatureGroup {
  groupName: string
  features: AppFeature[]
}

/**
 * A set of alike permissions that are variants of the same functionality.
 * Most often these variations are for 'viewing' and 'modifying'.
 *
 * For example...
 * viewRetailers: { permissions: ['retailers_v4.view'] }
 *
 * Or another example...
 * connectPlatform: { permissions: ['connect_platform.view', 'connect_platform.modify'] }
 */
export interface AppFeature {
  featureName: string
  permissionSlugs: string[]
}

/**
 * A flattened set of properties for a permission
 */
export interface AppPermission {
  readonly group: string
  readonly featureName: string
  readonly slug: string
  readonly madeExternalDate: Date // The date that the permission was made externally available
}

export type AppPermissionsKeys = keyof AppPermission

export const numberOfDaysAPermissionIsNew = 60

// Default date for permissions that were apart of the original release
const DEFAULT_DATE = new Date('1990-01-01')

/**
 * A flattened dictionary of the external permissions available on IPP.
 */
export const PermissionsDictionary = [
  {
    group: 'administration',
    featureName: 'basicAccess',
    slug: 'basic.legacy_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'administration',
    featureName: 'userManagement',
    slug: 'manage_users.legacy_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'administration',
    featureName: 'roleManagement',
    slug: 'role_management.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'administration',
    featureName: 'roleManagement',
    slug: 'role_management.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'administration',
    featureName: 'userActivity',
    slug: 'audit_logs.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'developers',
    featureName: 'devPortal',
    slug: 'dev_portal.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'developers',
    featureName: 'devAdmin',
    slug: 'dev_portal_admin.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'developers',
    featureName: 'devAdmin',
    slug: 'dev_portal_admin.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'developers',
    featureName: 'webhookConfig',
    slug: 'retailer_management.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'developers',
    featureName: 'webhookConfig',
    slug: 'retailer_management.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'developers',
    featureName: 'connectPlatform',
    slug: 'connect_platform.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'developers',
    featureName: 'connectPlatform',
    slug: 'connect_platform.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'merchandisingAndCollections',
    featureName: 'retailerCollections',
    slug: 'collections.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'merchandisingAndCollections',
    featureName: 'retailerCollections',
    slug: 'collections.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'merchandisingAndCollections',
    featureName: 'promotionalBanners',
    slug: 'promoted_placements.legacy_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'merchandisingAndCollections',
    featureName: 'placements',
    slug: 'content_management_storefront_placements.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'merchandisingAndCollections',
    featureName: 'placements',
    slug: 'content_management_storefront_placements.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'orders',
    featureName: 'lastMileDelivery',
    slug: 'connect_orders.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'orders',
    featureName: 'lastMileDelivery',
    slug: 'connect_orders.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'orders',
    featureName: 'lastMileDeliveryDetails',
    slug: 'retailers_v4.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'orders',
    featureName: 'delivery',
    slug: 'orders_delivery.legacy_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'orders',
    featureName: 'pickup',
    slug: 'orders_pickup.legacy_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'orders',
    featureName: 'orders',
    slug: 'orders_v4.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'orders',
    featureName: 'orders',
    slug: 'orders_v4.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'catalog',
    featureName: 'products',
    slug: 'catalog.legacy_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'catalog',
    featureName: 'images',
    slug: 'catalog_admin_image_management.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'catalog',
    featureName: 'images',
    slug: 'catalog_admin_image_management.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'catalog',
    featureName: 'codeLookup',
    slug: 'code_lookup_batches.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'pickupAnalytics',
    slug: 'pickup_analytics.legacy_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'insightsExecutiveSummary',
    slug: 'insights_portal.executive_summary',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'insightsPerformance',
    slug: 'insights_portal.performance',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'adsInsights',
    slug: 'insights_portal.view_ads_dashboard',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'retailerReports',
    slug: 'retailer_reports.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'retailerReports',
    slug: 'retailer_reports.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'dataExplorer',
    slug: 'insights_portal.data_explorer',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'retailerTaxLog',
    slug: 'normalized_reports.RETAILER_TAX_LOGS',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'taxesPaidAtPos',
    slug: 'normalized_reports.TAXES_PAID_AT_POS',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'mpfTaxesSemiMonthly',
    slug: 'normalized_reports.mpf_taxes_semi_monthly',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'adsReport',
    slug: 'normalized_reports.ADS_REPORT',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'prescriptionDeliveryReports',
    slug: 'rx_tlogs.legacy_warehouse_level_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'transactionLogs',
    slug: 'tlogs.legacy_warehouse_level_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'analyticsAndReports',
    featureName: 'alcoholTransactionLogs',
    slug: 'alcohol_tlogs.legacy_warehouse_level_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'staffPerformanceDashboard',
    slug: 'store_performance.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'staffPerformanceData',
    slug: 'historical_store_performance.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'warehouseLocationHours',
    slug: 'warehouse_location_hours.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'warehouseLocationHours',
    slug: 'warehouse_location_hours.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'retailerLocationGroups',
    slug: 'retailer_location_groups.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'retailerLocationGroups',
    slug: 'retailer_location_groups.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'shifts',
    slug: 'manage_shoppers.legacy_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'partnerPick',
    slug: 'partner_pick.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'partnerPick',
    slug: 'partner_pick.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'shoppers',
    slug: 'shoppers_retailer_settings.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'stores',
    featureName: 'shoppers',
    slug: 'shoppers_retailer_settings.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'integrations',
    featureName: 'integrationsPage',
    slug: 'integrations.view',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'integrations',
    featureName: 'integrationsPage',
    slug: 'integrations.modify',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'integrations',
    featureName: 'stripeIntegration',
    slug: 'stripe.legacy_permission',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'notifications',
    featureName: 'admin',
    slug: 'notifications.full_scope',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'notifications',
    featureName: 'insightsSummaryReport',
    slug: 'notifications.insights-summary-report',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'notifications',
    featureName: 'inventoryFileNormalization',
    slug: 'notifications.inventory-file-normalization',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'notifications',
    featureName: 'mpfTaxFileNotifications',
    slug: 'notifications.mpf-tax-file-notifications',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'notifications',
    featureName: 'fileIngestionCriticalDataValidation',
    slug: 'notifications.file-ingestion-critical-data-validation',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'notifications',
    featureName: 'dailyFileIngestionDataValidation',
    slug: 'notifications.daily-file-ingestion-data-validation',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'notifications',
    featureName: 'weeklyFileIngestionDataValidation',
    slug: 'notifications.weekly-file-ingestion-data-validation',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'notifications',
    featureName: 'storeHourUpdates',
    slug: 'notifications.store-hour-updates',
    madeExternalDate: DEFAULT_DATE,
  },
  {
    group: 'notifications',
    featureName: 'upcomingStoreHoursReminder',
    slug: 'notifications.upcoming-store-hours-reminder',
    madeExternalDate: DEFAULT_DATE,
  },

  /**
   * DEV NOTE: Add the permissions you want to expose externally in this structure above.
   * Please review with #team-retailer-tools first!
   * The order in which the features and permissions are listed here is how they will be presented on the UI.
   */
] as const satisfies readonly AppPermission[]

const permissionSlugExists = (permissionSlug: string) =>
  PermissionsDictionary.find(permission => permission.slug === permissionSlug) !== undefined

/** A dependency from all .modify permissions to their equivalent .view permission is automatically created
 * When a modify permission is checked the view permission is also checked
 * When a view permission is unchecked the modify permission is also unchecked
 */
const generateViewModifyPermissionDependencies = () => {
  const allPermissionSlugs = PermissionsDictionary.map(permission => permission.slug)

  const allModifyPermissionDependencies: Record<string, string[]> = {}

  allPermissionSlugs.forEach(slug => {
    // If the permission is a modify permission then add the equivalent view permission to the dependencies
    if (slug.endsWith('.modify')) {
      const viewPermissionSlug = slug.slice(0, -6).concat('view')
      if (permissionSlugExists(viewPermissionSlug)) {
        allModifyPermissionDependencies[slug]
          ? allModifyPermissionDependencies[slug].push(viewPermissionSlug)
          : (allModifyPermissionDependencies[slug] = [viewPermissionSlug])
      }
    }
  })

  return allModifyPermissionDependencies
}

/**
 * DEV NOTE: Add permission dependencies to the structure below
 * If the permission on the left is selected on the UI, then all the permissions on the right will automatically be forced to be selected.
 * If the permission on the right is unselected on the UI, then the permissions on the left will automatically be forced to be unselected.
 */
export const PermissionDependencies: Record<string, string[]> = {
  'normalized_reports.RETAILER_TAX_LOGS': ['retailer_reports.view'],
  'normalized_reports.TAXES_PAID_AT_POS': ['retailer_reports.view'],
  'normalized_reports.ADS_REPORT': ['retailer_reports.view'],
  'promoted_placements.legacy_permission': ['content_management_storefront_placements.view'],
  'content_management_storefront_placements.view': ['promoted_placements.legacy_permission'],
  'dev_portal.view': ['connect_platform.view', 'retailer_management.view'],
  'dev_portal_admin.view': ['connect_platform.modify', 'retailer_management.modify'],
  'retailer_management.view': ['dev_portal_admin.view'],
  ...generateViewModifyPermissionDependencies(),
}

// Create a record of all permission dependents to ensure they are unchecked when their child deoendency is disabled
const invertPermissionDependencies = () => {
  const allPermissionDependentsLocal: Record<string, string[]> = {}

  Object.entries(PermissionDependencies).map(([dependentKey, dependencies]) => {
    dependencies.forEach(dependency =>
      allPermissionDependentsLocal[dependency]
        ? allPermissionDependentsLocal[dependency].push(dependentKey)
        : (allPermissionDependentsLocal[dependency] = [dependentKey])
    )
  })

  return allPermissionDependentsLocal
}
export const PermissionDependents = invertPermissionDependencies()
